import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FileText, Download, Loader2, Shield, CheckCircle, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';
import type { AuditLogRecord, ComplianceAlert, ComplianceMetrics } from '@/types/compliance';
import {
  actionCategoryLabels,
  sensitivityLabels,
  alertSeverityLabels,
  alertStatusLabels,
} from '@/types/compliance';

interface ComplianceReportPDFProps {
  auditLogs?: AuditLogRecord[];
  alerts?: ComplianceAlert[];
  metrics?: ComplianceMetrics;
  period: number;
}

interface ReportConfig {
  title: string;
  auditor: string;
  includeAuditLog: boolean;
  includeAlerts: boolean;
  includeMetrics: boolean;
  includeIntegrity: boolean;
  maxAuditRecords: number;
}

export function ComplianceReportPDF({
  auditLogs,
  alerts,
  metrics,
  period,
}: ComplianceReportPDFProps) {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [config, setConfig] = useState<ReportConfig>({
    title: 'Informe de Compliance',
    auditor: '',
    includeAuditLog: true,
    includeAlerts: true,
    includeMetrics: true,
    includeIntegrity: true,
    maxAuditRecords: 100,
  });

  const generatePDF = async () => {
    setGenerating(true);

    try {
      // Dynamically import jsPDF
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();

      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let yPos = 20;

      const addNewPageIfNeeded = (neededSpace: number) => {
        if (yPos + neededSpace > doc.internal.pageSize.getHeight() - 20) {
          doc.addPage();
          yPos = 20;
          return true;
        }
        return false;
      };

      const drawLine = (y: number) => {
        doc.setDrawColor(200);
        doc.line(margin, y, pageWidth - margin, y);
      };

      // Header
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text(config.title, margin, yPos);
      yPos += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text('Sistema Athenea - Modulo INDRA', margin, yPos);
      yPos += 5;
      doc.text(`Generado: ${format(new Date(), "dd/MM/yyyy HH:mm", { locale: es })}`, margin, yPos);
      yPos += 5;
      doc.text(`Periodo: Ultimos ${period} dias`, margin, yPos);
      if (config.auditor) {
        yPos += 5;
        doc.text(`Auditor: ${config.auditor}`, margin, yPos);
      }
      yPos += 10;
      drawLine(yPos);
      yPos += 10;

      // Metrics Summary
      if (config.includeMetrics && metrics) {
        doc.setTextColor(0);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Resumen de Metricas', margin, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');

        const metricsData = [
          ['Total registros auditoria', metrics.audit_metrics.total_records?.toString() || '0'],
          ['Acciones de alto riesgo', metrics.audit_metrics.high_risk_actions?.toString() || '0'],
          ['Alertas abiertas', metrics.alert_metrics.open_alerts?.toString() || '0'],
          ['Alertas criticas', metrics.alert_metrics.critical_open?.toString() || '0'],
          ['Incumplimientos SLA', metrics.alert_metrics.sla_breaches?.toString() || '0'],
        ];

        metricsData.forEach(([label, value]) => {
          doc.text(label + ':', margin, yPos);
          doc.setFont('helvetica', 'bold');
          doc.text(value, margin + 60, yPos);
          doc.setFont('helvetica', 'normal');
          yPos += 6;
        });

        yPos += 5;
        drawLine(yPos);
        yPos += 10;
      }

      // Integrity Status
      if (config.includeIntegrity && metrics) {
        addNewPageIfNeeded(40);

        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Estado de Integridad', margin, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');

        const integrityStatus = metrics.integrity_status.chain_valid ? 'VERIFICADA' : 'COMPROMETIDA';
        const statusColor = metrics.integrity_status.chain_valid ? [34, 197, 94] : [239, 68, 68];

        doc.text('Cadena de hashes SHA-256:', margin, yPos);
        doc.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
        doc.setFont('helvetica', 'bold');
        doc.text(integrityStatus, margin + 55, yPos);
        doc.setTextColor(0);
        doc.setFont('helvetica', 'normal');
        yPos += 6;

        doc.text(`Registros verificados: ${metrics.integrity_status.records_checked}`, margin, yPos);
        yPos += 6;
        doc.text(`Ultima verificacion: ${format(new Date(metrics.integrity_status.last_check), "dd/MM/yyyy HH:mm", { locale: es })}`, margin, yPos);
        yPos += 10;
        drawLine(yPos);
        yPos += 10;
      }

      // Category Distribution
      if (config.includeMetrics && metrics?.audit_metrics.by_category) {
        addNewPageIfNeeded(60);

        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Distribucion por Categoria', margin, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');

        Object.entries(metrics.audit_metrics.by_category).forEach(([category, count]) => {
          const label = actionCategoryLabels[category as keyof typeof actionCategoryLabels] || category;
          doc.text(`${label}: ${count}`, margin, yPos);
          yPos += 5;
        });

        yPos += 5;
        drawLine(yPos);
        yPos += 10;
      }

      // Alerts Summary
      if (config.includeAlerts && alerts && alerts.length > 0) {
        addNewPageIfNeeded(40);

        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`Alertas de Compliance (${alerts.length})`, margin, yPos);
        yPos += 10;

        doc.setFontSize(9);

        // Table header
        doc.setFont('helvetica', 'bold');
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, yPos - 4, pageWidth - 2 * margin, 8, 'F');
        doc.text('Severidad', margin + 2, yPos);
        doc.text('Titulo', margin + 25, yPos);
        doc.text('Estado', margin + 110, yPos);
        doc.text('Fecha', margin + 140, yPos);
        yPos += 8;

        doc.setFont('helvetica', 'normal');

        const openAlerts = alerts.filter(a => a.status === 'open');
        const resolvedAlerts = alerts.filter(a => a.status === 'resolved');
        const displayAlerts = [...openAlerts, ...resolvedAlerts].slice(0, 20);

        displayAlerts.forEach((alert) => {
          if (addNewPageIfNeeded(8)) {
            // Reprint header on new page
            doc.setFont('helvetica', 'bold');
            doc.setFillColor(240, 240, 240);
            doc.rect(margin, yPos - 4, pageWidth - 2 * margin, 8, 'F');
            doc.text('Severidad', margin + 2, yPos);
            doc.text('Titulo', margin + 25, yPos);
            doc.text('Estado', margin + 110, yPos);
            doc.text('Fecha', margin + 140, yPos);
            yPos += 8;
            doc.setFont('helvetica', 'normal');
          }

          const severityLabel = alertSeverityLabels[alert.severity] || alert.severity;
          const statusLabel = alertStatusLabels[alert.status] || alert.status;
          const titleTrunc = alert.title.length > 45 ? alert.title.substring(0, 42) + '...' : alert.title;

          // Severity color
          if (alert.severity === 'critical') doc.setTextColor(239, 68, 68);
          else if (alert.severity === 'high') doc.setTextColor(249, 115, 22);
          else doc.setTextColor(0);

          doc.text(severityLabel, margin + 2, yPos);
          doc.setTextColor(0);
          doc.text(titleTrunc, margin + 25, yPos);
          doc.text(statusLabel, margin + 110, yPos);
          doc.text(format(new Date(alert.detected_at), 'dd/MM/yy', { locale: es }), margin + 140, yPos);
          yPos += 6;
        });

        if (alerts.length > 20) {
          yPos += 2;
          doc.setTextColor(100);
          doc.text(`... y ${alerts.length - 20} alertas mas`, margin, yPos);
          doc.setTextColor(0);
        }

        yPos += 10;
        drawLine(yPos);
        yPos += 10;
      }

      // Audit Log Sample
      if (config.includeAuditLog && auditLogs && auditLogs.length > 0) {
        addNewPageIfNeeded(40);

        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`Registro de Auditoria (${auditLogs.length} registros)`, margin, yPos);
        yPos += 10;

        doc.setFontSize(8);

        // Table header
        doc.setFont('helvetica', 'bold');
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, yPos - 4, pageWidth - 2 * margin, 8, 'F');
        doc.text('#', margin + 2, yPos);
        doc.text('Entidad', margin + 12, yPos);
        doc.text('Accion', margin + 45, yPos);
        doc.text('Resumen', margin + 70, yPos);
        doc.text('Usuario', margin + 130, yPos);
        doc.text('Fecha', margin + 160, yPos);
        yPos += 8;

        doc.setFont('helvetica', 'normal');

        const displayLogs = auditLogs.slice(0, config.maxAuditRecords);

        displayLogs.forEach((log) => {
          if (addNewPageIfNeeded(6)) {
            // Reprint header
            doc.setFont('helvetica', 'bold');
            doc.setFillColor(240, 240, 240);
            doc.rect(margin, yPos - 4, pageWidth - 2 * margin, 8, 'F');
            doc.text('#', margin + 2, yPos);
            doc.text('Entidad', margin + 12, yPos);
            doc.text('Accion', margin + 45, yPos);
            doc.text('Resumen', margin + 70, yPos);
            doc.text('Usuario', margin + 130, yPos);
            doc.text('Fecha', margin + 160, yPos);
            yPos += 8;
            doc.setFont('helvetica', 'normal');
          }

          const actionLabel = actionCategoryLabels[log.action_category] || log.action_category;
          const summaryTrunc = (log.change_summary || '').substring(0, 35);
          const userTrunc = (log.performed_by_name || '').substring(0, 15);

          // Sensitivity color
          if (log.sensitivity_level === 'critical') doc.setTextColor(239, 68, 68);
          else if (log.sensitivity_level === 'high') doc.setTextColor(249, 115, 22);
          else doc.setTextColor(0);

          doc.text(log.sequence_number.toString(), margin + 2, yPos);
          doc.setTextColor(0);
          doc.text(log.entity_type.substring(0, 15), margin + 12, yPos);
          doc.text(actionLabel, margin + 45, yPos);
          doc.text(summaryTrunc, margin + 70, yPos);
          doc.text(userTrunc, margin + 130, yPos);
          doc.text(format(new Date(log.timestamp_utc), 'dd/MM HH:mm', { locale: es }), margin + 160, yPos);
          yPos += 5;
        });

        if (auditLogs.length > config.maxAuditRecords) {
          yPos += 2;
          doc.setTextColor(100);
          doc.text(`... y ${auditLogs.length - config.maxAuditRecords} registros mas`, margin, yPos);
          doc.setTextColor(0);
        }
      }

      // Footer on last page
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Pagina ${i} de ${pageCount} - Documento generado automaticamente por Athenea INDRA`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }

      // Save the PDF
      const fileName = `compliance-report-${format(new Date(), 'yyyy-MM-dd-HHmm')}.pdf`;
      doc.save(fileName);

      toast({
        title: 'Reporte generado',
        description: `El archivo ${fileName} se ha descargado`,
      });

      setDialogOpen(false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: 'Error',
        description: 'No se pudo generar el reporte PDF',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setDialogOpen(true)}>
        <FileText className="h-4 w-4 mr-2" />
        Generar Reporte
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Generar Reporte de Compliance
            </DialogTitle>
            <DialogDescription>
              Configura el contenido del informe PDF para auditoria
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Titulo del Reporte</Label>
              <Input
                id="title"
                value={config.title}
                onChange={(e) => setConfig({ ...config, title: e.target.value })}
                placeholder="Informe de Compliance"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="auditor">Nombre del Auditor (opcional)</Label>
              <Input
                id="auditor"
                value={config.auditor}
                onChange={(e) => setConfig({ ...config, auditor: e.target.value })}
                placeholder="Nombre del auditor"
              />
            </div>

            <div className="space-y-3">
              <Label>Secciones a incluir</Label>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="metrics"
                  checked={config.includeMetrics}
                  onCheckedChange={(checked) =>
                    setConfig({ ...config, includeMetrics: checked as boolean })
                  }
                />
                <label htmlFor="metrics" className="text-sm cursor-pointer">
                  Metricas y estadisticas
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="integrity"
                  checked={config.includeIntegrity}
                  onCheckedChange={(checked) =>
                    setConfig({ ...config, includeIntegrity: checked as boolean })
                  }
                />
                <label htmlFor="integrity" className="text-sm cursor-pointer">
                  Estado de integridad (hash chain)
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="alerts"
                  checked={config.includeAlerts}
                  onCheckedChange={(checked) =>
                    setConfig({ ...config, includeAlerts: checked as boolean })
                  }
                />
                <label htmlFor="alerts" className="text-sm cursor-pointer">
                  Alertas de compliance ({alerts?.length || 0})
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="auditLog"
                  checked={config.includeAuditLog}
                  onCheckedChange={(checked) =>
                    setConfig({ ...config, includeAuditLog: checked as boolean })
                  }
                />
                <label htmlFor="auditLog" className="text-sm cursor-pointer">
                  Registro de auditoria ({auditLogs?.length || 0})
                </label>
              </div>
            </div>

            {config.includeAuditLog && (
              <div className="space-y-2">
                <Label htmlFor="maxRecords">Max registros de auditoria</Label>
                <Select
                  value={config.maxAuditRecords.toString()}
                  onValueChange={(v) => setConfig({ ...config, maxAuditRecords: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50 registros</SelectItem>
                    <SelectItem value="100">100 registros</SelectItem>
                    <SelectItem value="250">250 registros</SelectItem>
                    <SelectItem value="500">500 registros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={generatePDF} disabled={generating}>
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Descargar PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
